/**
 * flowConfigSchema.js
 *
 * Defines the default shapes and field documentation for the two Firestore
 * fields added to every vtec_assets factory document during the mqttConfig.js
 * → Firestore migration:
 *
 *   unsConfig   — MQTT broker credentials + topic branches
 *   flowConfig  — ReactFlow architecture diagram nodes and background boxes
 *
 * These defaults are used by:
 *   - FactoryOne.js  (new-factory form initialisation)
 *   - UnsFlow.js     (reading flowConfig from Firestore, falling back to empty)
 *   - ArchEditor.js  (admin drag-and-drop diagram editor — future AC)
 *
 * Collection:  vtec_assets
 * Document ID: {factoryId}  (same document as the rest of the factory data)
 */

// ---------------------------------------------------------------------------
// unsConfig default
// Replaces: mqttConfigurations[factoryId] + flowTopicMap[factoryId]
// ---------------------------------------------------------------------------

/**
 * Default shape for the unsConfig embedded object.
 *
 * Fields
 * ──────
 * enabled         {boolean}   Toggle UNS/MQTT functionality for this factory.
 *
 * mqttConfig      {object}    MQTT broker connection details.
 *   broker        {string}    WebSocket broker URL, e.g. "wss://…:8884/mqtt".
 *   username      {string}    MQTT username.
 *   password      {string}    MQTT password.
 *   topicPrefix   {string}    Base path prepended to every topicBranch before
 *                             subscribing.  Empty string "" is valid (ProveIt).
 *
 * siteFilters     {object[]}  Optional multi-site filter list.
 *   [].id         {string}    Filter key used in dynamic topic routing.
 *   [].label      {string}    Display label shown in the UNS sidebar dropdown.
 *
 * topicBranches   {string[]}  Branch patterns appended to topicPrefix.
 *                             Wildcards (# and +) are kept verbatim.
 *                             Patterns without wildcards get "/#" appended by
 *                             the subscribe helper (matches old buildTopicPath).
 *
 * defaultFilter   {string}    Initial siteFilter key shown on load.
 */
export const defaultUnsConfig = {
  enabled: false,
  mqttConfig: {
    broker: "",
    username: "",
    password: "",
    topicPrefix: "",
  },
  siteFilters: [],
  topicBranches: ["#"],
  defaultFilter: "overall",
};

// ---------------------------------------------------------------------------
// flowConfig default
// Replaces: flowDataArray[factoryId] in UnsFlow.js
// ---------------------------------------------------------------------------

/**
 * Default shape for the flowConfig embedded object.
 *
 * Fields
 * ──────
 * title           {string}    Internal / display name for this flow diagram.
 * description     {string}    Human-readable description.
 *
 * flowNodes       {object[]}  One entry per technology node visible in the
 *                             ReactFlow canvas.  A node entry with a non-null
 *                             "target" also generates one directed edge.
 *
 *   [].id             {string}         Unique node id within this diagram.
 *                                      Used as ReactFlow node.id.
 *                                      ⚠ Important: "id" identifies the rendered
 *                                      node position, while "source" (below)
 *                                      identifies where the outgoing edge starts.
 *                                      These are usually identical but can differ
 *                                      (e.g. the Tulip app node is positioned at
 *                                      id="tulip" but its edge originates from
 *                                      source="hivemq").
 *
 *   [].source         {string}         The ReactFlow edge source node id.
 *                                      Defaults to the same value as "id".
 *                                      Set to a different node id when the edge
 *                                      for this diagram entry originates from
 *                                      a different node than the one rendered.
 *
 *   [].img            {string}         Image URL.  Either a Tulip CDN URL
 *                                      (tulip.widen.net) or a Firebase Storage
 *                                      URL (firebasestorage.googleapis.com).
 *
 *   [].topic          {string|string[]} MQTT topic fragment(s) used for live
 *                                      edge-highlighting.  When a received MQTT
 *                                      message topic includes this value the
 *                                      connecting edge is highlighted yellow
 *                                      (#F2EEA1) by UnsEdge.js.
 *
 *   [].protocol       {string|null}    Edge label — the connection protocol.
 *                                      Typical values:
 *                                        "MQTT", "OPC UA", "MODBUS", "REST API",
 *                                        "Edge Driver", "USB", "Table Updates"
 *                                      null → no label rendered on the edge.
 *
 *   [].sourcePosition {string}         Handle direction when this node acts as
 *                                      a connection source.
 *                                      One of: "right"|"left"|"top"|"bottom"
 *
 *   [].targetPosition {string|null}    Handle direction when this node acts as
 *                                      a connection target.
 *                                      null for source-only nodes.
 *
 *   [].target         {string|null}    id of the destination node for the
 *                                      outgoing edge.  null for sink nodes.
 *
 *   [].sourceOverride {string|null}    When non-null, overrides sourcePosition
 *                                      for the edge only (the node's own handle
 *                                      is unaffected).  Used when a node's
 *                                      outgoing edge exits from a different side
 *                                      than its default handle.
 *
 *   [].active         {boolean}        Initial visual active state.  Always
 *                                      false in Firestore; toggled at runtime
 *                                      by handleNodeClick.
 *
 *   [].position       {{ x, y }}       ReactFlow canvas coordinates in pixels.
 *                                      Updated by the admin drag-and-drop
 *                                      editor and persisted back to Firestore.
 *
 * backgroundBoxes {object[]}  Section background containers rendered behind
 *                             the flow nodes (zIndex: -1, non-interactive).
 *
 *   [].id         {string}    Unique id.  Convention: "background-{label}".
 *   [].position   {{ x, y }} Canvas position in pixels.
 *   [].label      {string}   Section title, e.g. "Physical", "Cloud".
 *   [].imageUrl   {string|null} Optional header icon URL.
 *   [].width      {number}   Box width in pixels.
 *   [].height     {number}   Box height in pixels.
 */
export const defaultFlowConfig = {
  title: "",
  description: "",
  flowNodes: [],
  backgroundBoxes: [],
};

// ---------------------------------------------------------------------------
// Empty node/box templates — used by the drag-and-drop editor (future AC)
// ---------------------------------------------------------------------------

/**
 * Template for a new flow node created via the admin editor.
 * Clone this object and fill in the factory-specific values.
 */
export const newFlowNodeTemplate = {
  id: "",
  source: "",        // defaults to id value; set differently only for split-source nodes
  img: "",
  topic: "",
  protocol: null,
  sourcePosition: "right",
  targetPosition: null,
  target: null,
  sourceOverride: null,
  active: false,
  position: { x: 0, y: 0 },
};

/**
 * Template for a new background box created via the admin editor.
 */
export const newBackgroundBoxTemplate = {
  id: "",
  position: { x: 0, y: 0 },
  label: "",
  imageUrl: null,
  width: 200,
  height: 200,
};

// ---------------------------------------------------------------------------
// Valid enum values (for editor dropdowns / validation)
// ---------------------------------------------------------------------------

/** Valid ReactFlow handle positions */
export const HANDLE_POSITIONS = ["right", "left", "top", "bottom"];

/** Connection protocol labels used as edge labels */
export const PROTOCOLS = [
  "MQTT",
  "OPC UA",
  "MODBUS",
  "REST API",
  "Edge Driver",
  "USB",
  "Table Updates",
  "PHIDGET",
  "RS232",
  "XML",
  "MQTT Bridge",
];
